import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Stack, Alert } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import usePermission from '@/hooks/usePermission'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import NotFound from '@/pages/NotFound'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { notNull } from '@/utils/typescript'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'
import Drawer from '@/components/drawer/Drawer'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import SettingsNavigation from '@/components/settings-navigation/SettingsNavigation'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import { useGetIntegrations } from '@/service-library/hooks/integrations'
import AddIntegrationButton from './AddIntegrationButton'

const qualityRoutes: RouteDefinition[] = [
  {
    to: 'document-rules',
    label: 'Document Rules',
    keepQueryParams: true,
  },
  {
    to: 'field-rules',
    label: 'Field Rules',
    keepQueryParams: true,
  },
]

type RouteDefinition = {
  to: string
  label: string
  isSuperUserOnly?: boolean
  keepQueryParams?: boolean
}

export default function ProjectSettingsLayout() {
  const featureFlags = useFeatureFlagContext()
  const { hasEditingPermission } = usePermission()
  const { project } = useProjectContext()
  const [demoMode] = useDemoModeContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const [errors, setErrors] = useState<string[] | null>(null)
  const drawerOverlay = useOverlay({ initiallyOpen: true })
  const isSuperUser = useIsSuperUser()

  const { integrations, queryKey } = useGetIntegrations({
    filters: {
      limit: '100',
      project_id: project.id,
      fields__only: 'id,name,is_enabled,integration_type_id',
    },
  })

  const routes: RouteDefinition[] = [
    {
      to: 'general',
      label: 'General',
      keepQueryParams: true,
    },
    {
      to: 'sources',
      label: 'Sources',
      keepQueryParams: true,
    },
    {
      to: 'fields',
      label: 'Fields',
      keepQueryParams: true,
    },
    featureFlags.model_library
      ? {
          to: 'model-library',
          label: 'Model Library',
          keepQueryParams: true,
        }
      : null,
    {
      to: 'tags',
      label: 'Document Tags',
      keepQueryParams: true,
    },
    !demoMode && isSuperUser
      ? {
          to: 'model-tables',
          label: 'Model Tables',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
    !demoMode && isSuperUser
      ? {
          to: 'advanced-settings',
          label: 'Advanced Settings',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
  ].filter(Boolean) as RouteDefinition[]

  const workflowRoutes: RouteDefinition[] = [
    {
      to: 'workflow-general',
      label: 'General',
      keepQueryParams: true,
    },
    {
      to: 'workflow-states',
      label: 'Workflow States',
      keepQueryParams: true,
    },
    selectedWorkflow.project_model_id
      ? {
          to: 'model-configuration',
          label: 'Model Configuration',
          keepQueryParams: true,
        }
      : null,
  ].filter(notNull)

  const integrationRoutes: RouteDefinition[] = integrations.map(
    (integration) => ({
      to: `integrations/${integration.id}`,
      label: integration.name,
      keepQueryParams: true,
    }),
  )

  const canEditProject = hasEditingPermission('edit_projects', project.org_id)

  if (!canEditProject) return <NotFound />

  return (
    <>
      <ProjectBreadcrumb
        label="Settings"
        url={`/projects/${project.id}/settings/general`}
      />
      <Drawer overlay={drawerOverlay} size="sm" transparent>
        <SettingsNavigation label="Project Settings" routes={routes} />
        <SettingsNavigation label="Quality Control" routes={qualityRoutes} />
        <SettingsNavigation
          label="Workflow Settings"
          routes={workflowRoutes}
          subHeader={selectedWorkflow.name}
        />
        <SettingsNavigation
          label="Integrations"
          routes={integrationRoutes}
          actions={
            <>
              <AddIntegrationButton listQueryKey={queryKey} />
            </>
          }
        />
      </Drawer>

      <Stack
        direction="column"
        p={2}
        pt={3}
        width="100%"
        flexGrow={1}
        sx={{ minHeight: 0 }}
      >
        {errors && <Alert severity="error">{errors}</Alert>}
        <Outlet
          context={{
            setWizardErrors: setErrors,
            isSettings: true,
          }}
        />
      </Stack>
    </>
  )
}
